import parser, { type DOMNode, type Element, type Text } from 'html-react-parser'

// Default-deny allowlist. Only inert formatting and link tags render as HTML; every other
// tag (script, iframe, object, embed, svg, style, form, input, ...) is escaped to text by
// the replace() handler below, so inbound message content cannot execute. See SWR-24405.
export const htmlTags = [
  'a',
  'b',
  'br',
  'code',
  'div',
  'em',
  'i',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
  'ul',
  'wbr',
]

type IParseHtmlOptions = {
  search?: string
}

const escapeRegExp = (value: string) => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

// Only these URL schemes may render on a link. Anything else (javascript:, data:,
// vbscript:, ...) is dropped. Scheme-less links (relative paths, anchors) are allowed.
const SAFE_URL_SCHEME = /^(https?|mailto|tel|sms):/i

const isSafeHref = (raw: string): boolean => {
  // Browsers ignore ASCII whitespace and control characters inside URLs, so strip them
  // before checking the scheme. Otherwise `jav&#x09;ascript:` slips past as javascript:.
  const url = Array.from(raw)
    .filter((char) => {
      const code = char.charCodeAt(0)
      return code > 0x20 && (code < 0x7f || code > 0x9f)
    })
    .join('')

  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) return SAFE_URL_SCHEME.test(url)

  return true
}

export const parserHtml = (text: string, options?: IParseHtmlOptions) => {
  const { search } = options || {}

  return parser(text, {
    replace(domNode: DOMNode) {
      const textNode = domNode as Text
      const tagNode = domNode as Element
      // htmlparser2 gives <script> and <style> their own node types, not 'tag'.
      const isElement =
        domNode.type === 'tag' || domNode.type === 'script' || domNode.type === 'style'
      const isText = domNode.type === 'text'
      const isValid = htmlTags.includes(tagNode.name)

      if (!isValid && isElement) {
        const htmlContent = parser(`&lt;${tagNode.name}&gt;`)
        const nextElements = tagNode.children.map((item) => {
          if (item.type === 'tag') return ''

          return (item as Text).data
        })

        return (
          <>
            {htmlContent} {nextElements}
          </>
        )
      }

      if (isElement && tagNode.name === 'a' && tagNode.attribs?.href) {
        if (!isSafeHref(tagNode.attribs.href)) {
          delete tagNode.attribs.href
        }
      }

      if (search && isText && textNode.data.toLowerCase().includes(search.toLowerCase())) {
        const escapedSearchTerm = escapeRegExp(search)
        const regExp = new RegExp(escapedSearchTerm, 'gi')
        const parts = textNode.data.split(regExp)
        const matches = textNode.data.match(regExp) ?? []

        return (
          <>
            {parts.map((part, i) => (
              <>
                {part}
                {i < matches.length && (
                  <mark style={{ backgroundColor: 'var(--yellow-40)' }}>{matches[i]}</mark>
                )}
              </>
            ))}
          </>
        )
      }
    },
  })
}
